import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Calendar } from 'lucide-react';

export const PageCard = memo(function PageCard({ page }) {
  const secondaryText = Array.isArray(page.secondary_type_page)
    ? page.secondary_type_page.join(", ")
    : typeof page.secondary_type_page === "object" && page.secondary_type_page !== null
      ? Object.entries(page.secondary_type_page)
          .map(([key, val]) => `${key.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}: ${val}`)
          .join(" | ")
      : String(page.secondary_type_page || "");

  // Type badge colors - more vibrant for dark mode
  const getTypeBadgeClasses = (type) => {
    const types = {
      'feature': 'bg-blue-500 dark:bg-blue-600',
      'brand': 'bg-purple-500 dark:bg-purple-600',
      'budget': 'bg-emerald-500 dark:bg-emerald-600',
      'use-case': 'bg-orange-500 dark:bg-orange-600',
      'trending': 'bg-pink-500 dark:bg-pink-600',
      'deal': 'bg-red-500 dark:bg-red-600',
    };
    return types[type] || 'bg-slate-500 dark:bg-slate-600';
  };

  return (
    <Link
      to={`/${page.slug}`}
      className="group block bg-white dark:bg-[#0f1729] rounded-xl border border-slate-200 dark:border-[#1e2d4a] shadow-sm hover:shadow-xl dark:hover:shadow-2xl dark:hover:shadow-blue-900/20 transition-all duration-300 hover:-translate-y-1 overflow-hidden"
    >
      {/* Image Section */}
      <div className="relative h-44 overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200 dark:from-[#121c30] dark:to-[#0c1222]">
        <img
          src={`https://placehold.co/600x400/1a2744/6b8cce?text=${encodeURIComponent(secondaryText)}`}
          alt={`${page.primary_type_page} Preview`}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          loading="lazy"
        />
        
        {/* Gradient overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />

        {/* Type badge */}
        <div className={`absolute top-3 left-3 ${getTypeBadgeClasses(page.primary_type_page)} text-white text-xs font-semibold px-2.5 py-1 rounded-full shadow-lg`}>
          {page.primary_type_page}
        </div>
      </div>

      {/* Content Section */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors line-clamp-2 leading-snug">
            {page.title}
          </h3>
          <ExternalLink className="w-4 h-4 text-slate-400 dark:text-slate-500 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors flex-shrink-0 mt-0.5" />
        </div>

        <p className="text-slate-600 dark:text-slate-400 text-sm mb-3 line-clamp-2 leading-relaxed">
          {page.description}
        </p>

        <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-500">
          <Calendar className="w-3.5 h-3.5" />
          <span>{new Date(page.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
        </div>
      </div>
    </Link>
  );
});